import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Home as HomeIcon, LogOut, Settings, Image, BarChart, Link2, History, Layout } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ArticlesManager from './ArticlesManager';
import ResourcesManager from './ResourcesManager';
import HeroEditor from './HeroEditor';
import ArticleImporter from './ArticleImporter';
import ArticleChangelog from './ArticleChangelog';
import TemplateManager from './TemplateManager';

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Access is enforced by the unified JWT ProtectedRoute wrapper — no legacy token gate.

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: BarChart },
    { id: 'importer', label: 'Article Importer', icon: Link2 },
    { id: 'templates', label: 'Template Manager', icon: Layout },
    { id: 'changelog', label: 'Changelog', icon: History },
    { id: 'articles', label: 'Articles', icon: FileText },
    { id: 'resources', label: 'Resources', icon: Download },
    { id: 'hero', label: 'Hero Content', icon: HomeIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-gray-900 to-purple-900 text-white flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
            INSPIYAH
          </h1>
          <p className="text-sm text-gray-400 mt-1">Admin Portal</p>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                activeSection === item.id
                  ? 'bg-white/20 text-white shadow-lg'
                  : 'text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 space-y-2">
          <a
            href="/"
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/10 hover:text-white transition-all"
          >
            <HomeIcon size={20} />
            <span>View Website</span>
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-all"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <h2 className="text-3xl font-bold text-gray-900">
            {menuItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
          </h2>
        </div>

        {/* Content Area */}
        <div className="p-8">
          {activeSection === 'overview' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-6 text-white">
                <Link2 size={32} className="mb-4" />
                <h3 className="text-2xl font-bold mb-2">Importer</h3>
                <p className="text-purple-100">Import articles from URLs</p>
                <button
                  onClick={() => setActiveSection('importer')}
                  className="mt-4 px-4 py-2 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-all"
                  data-testid="importer-card-button"
                >
                  Import →
                </button>
              </div>

              <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-6 text-white">
                <Layout size={32} className="mb-4" />
                <h3 className="text-2xl font-bold mb-2">Templates</h3>
                <p className="text-teal-100">Manage article templates</p>
                <button
                  onClick={() => setActiveSection('templates')}
                  className="mt-4 px-4 py-2 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-all"
                  data-testid="templates-card-button"
                >
                  Manage →
                </button>
              </div>

              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-6 text-white">
                <History size={32} className="mb-4" />
                <h3 className="text-2xl font-bold mb-2">Changelog</h3>
                <p className="text-indigo-100">Track all content changes</p>
                <button
                  onClick={() => setActiveSection('changelog')}
                  className="mt-4 px-4 py-2 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-all"
                  data-testid="changelog-card-button"
                >
                  View →
                </button>
              </div>

              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
                <FileText size={32} className="mb-4" />
                <h3 className="text-2xl font-bold mb-2">Articles</h3>
                <p className="text-blue-100">Manage blog posts and content</p>
                <button
                  onClick={() => setActiveSection('articles')}
                  className="mt-4 px-4 py-2 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-all"
                >
                  Manage →
                </button>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white">
                <Download size={32} className="mb-4" />
                <h3 className="text-2xl font-bold mb-2">Resources</h3>
                <p className="text-green-100">Manage downloadable content</p>
                <button
                  onClick={() => setActiveSection('resources')}
                  className="mt-4 px-4 py-2 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-all"
                >
                  Manage →
                </button>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
                <HomeIcon size={32} className="mb-4" />
                <h3 className="text-2xl font-bold mb-2">Hero Content</h3>
                <p className="text-orange-100">Update homepage hero section</p>
                <button
                  onClick={() => setActiveSection('hero')}
                  className="mt-4 px-4 py-2 bg-white/20 rounded-full text-sm hover:bg-white/30 transition-all"
                >
                  Edit →
                </button>
              </div>
            </div>
          )}

          {activeSection === 'importer' && <ArticleImporter />}
          {activeSection === 'templates' && <TemplateManager />}
          {activeSection === 'changelog' && <ArticleChangelog />}
          {activeSection === 'articles' && <ArticlesManager />}
          {activeSection === 'resources' && <ResourcesManager />}
          {activeSection === 'hero' && <HeroEditor />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;